const PR_TIMEZONE = "America/Puerto_Rico";
const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

const PR_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: PR_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const ISO_TZ_REGEX = /([+-]\d{2}:\d{2}|Z)$/;

function parseLocalIso(iso) {
  if (typeof iso !== "string") return null;
  const match = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  return {
    year,
    month,
    day,
    hour: Number(hour || 0),
    minute: Number(minute || 0),
    second: Number(second || 0),
  };
}

function dayIndexFromDateKey(dateKey) {
  const [year, month, day] = String(dateKey || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return utcDate.getUTCDay();
}

function getPrParts(input) {
  if (!input) return null;
  let iso = input;
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return null;
    iso = input.toISOString();
  }
  if (typeof iso !== "string") return null;

  if (!ISO_TZ_REGEX.test(iso)) {
    const parsed = parseLocalIso(iso);
    if (!parsed) return null;
    const dateKey = `${parsed.year}-${parsed.month}-${parsed.day}`;
    return {
      dateKey,
      year: parsed.year,
      month: parsed.month,
      day: parsed.day,
      hour: parsed.hour,
      minute: parsed.minute,
      second: parsed.second,
      minutesTotal: parsed.hour * 60 + parsed.minute,
    };
  }

  const sourceDate = new Date(iso);
  if (Number.isNaN(sourceDate.getTime())) return null;
  const parts = PR_FORMATTER.formatToParts(sourceDate);
  const lookup = (type) => parts.find((p) => p.type === type)?.value || "00";
  const year = lookup("year");
  const month = lookup("month");
  const day = lookup("day");
  const hour = Number(lookup("hour"));
  const minute = Number(lookup("minute"));
  const second = Number(lookup("second"));
  return {
    dateKey: `${year}-${month}-${day}`,
    year,
    month,
    day,
    hour,
    minute,
    second,
    minutesTotal: hour * 60 + minute,
  };
}

export function toPrISOString(date) {
  if (!(date instanceof Date)) return date;
  const parts = getPrParts(date.toISOString());
  if (!parts) return date.toISOString();
  const hour = String(parts.hour).padStart(2, "0");
  const minute = String(parts.minute).padStart(2, "0");
  const second = String(parts.second).padStart(2, "0");
  return `${parts.dateKey}T${hour}:${minute}:${second}`;
}

export function isoToPrDate(iso) {
  const dateKey = formatIsoPrDateKey(iso);
  if (!dateKey) return null;
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
}

export function formatIsoPrTime(iso, options = {}) {
  const parts = getPrParts(iso);
  if (!parts) return "";
  const minutesStr = String(parts.minute).padStart(2, "0");
  if (options.hour12) {
    const suffix = parts.hour >= 12 ? "PM" : "AM";
    const hours12 = ((parts.hour + 11) % 12) + 1;
    return `${hours12}:${minutesStr} ${suffix}`;
  }
  return `${String(parts.hour).padStart(2, "0")}:${minutesStr}`;
}

export function formatIsoPrDay(iso) {
  const parts = getPrParts(iso);
  if (!parts) return "";
  const index = dayIndexFromDateKey(parts.dateKey);
  return Number.isInteger(index) ? DIAS[index] : "";
}

export function formatIsoPrDateKey(iso) {
  const parts = getPrParts(iso);
  return parts ? parts.dateKey : "";
}

export function addDaysToDateKey(dateKey, days) {
  const [year, month, day] = String(dateKey || "").split("-").map(Number);
  if (!year || !month || !day) return "";
  const base = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().split("T")[0];
}

export function toPrCalendarIso(iso) {
  const parts = getPrParts(iso);
  if (!parts) return iso;
  const hour = String(parts.hour).padStart(2, "0");
  const minute = String(parts.minute).padStart(2, "0");
  return `${parts.dateKey}T${hour}:${minute}:00`;
}

export { PR_TIMEZONE };
